import React, { useState, useMemo } from 'react';
import { AlertCircle, ShieldCheck, Search } from 'lucide-react';
import Loader from '../Loader';
import useSienaguard from '../../hooks/useSienaguard';
import { useTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { IPInfo } from '../../api/sienaguardService';
import IPCountriesMap from './IPCountriesMap';
import TopCountries from './TopCountries';

const PAGE_SIZE = 3; // Número de dominios por página

const PhishingAttempts: React.FC = () => {
  const { t } = useTranslation();
  const { foundDomains, services, serviceDetailsMap, loading, error } = useSienaguard();

  const [selectedService, setSelectedService] = useState<string>('all');
  const [page, setPage] = useState<number>(1);

  // Lista principal
  const defaultDomains = foundDomains ?? [];

  // Filtrado según servicio seleccionado
  const filteredDomains = selectedService === 'all'
    ? defaultDomains
    : defaultDomains.filter((item) => item.serviceDomain.toLowerCase() === selectedService.toLowerCase());

  const totalAttempts = filteredDomains.length;
  const totalPages = Math.ceil(totalAttempts / PAGE_SIZE);
  const currentPage = Math.min(page, totalPages || 1);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pagedDomains = filteredDomains.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) setPage(p => p - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setPage(p => p + 1);
  };

  // Data para la gráfica (intentos por día)
  const apexData = useMemo(() => {
    const dailyData = new Map<string, { count: number; domains: string[] }>();
    filteredDomains.forEach((item) => {
      const day = new Date(item.runDate).toISOString().split('T')[0];
      const entry = dailyData.get(day);
      if (!entry) {
        dailyData.set(day, { count: 1, domains: [item.domain] });
      } else {
        entry.count += 1;
        entry.domains.push(item.domain);
      }
    });
    return Array.from(dailyData.entries())
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
      .map(([day, data]) => ({
        x: new Date(day).getTime(),
        y: data.count,
        domains: data.domains,
      }));
  }, [filteredDomains]);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar' as const,
      background: 'transparent',
      foreColor: '#333',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: { borderRadius: 4, horizontal: false },
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: 'datetime' as const,
      labels: { format: 'dd/MM' },
      axisBorder: { color: '#ccc' },
      axisTicks: { color: '#ccc' },
    },
    yaxis: {
      labels: { style: { colors: ['#666'] } },
    },
    tooltip: {
      theme: 'light',
      x: { format: 'dd/MM/yyyy' },
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const dataPoint = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        return `
          <div class="px-4 py-2 text-left">
            <div class="font-bold mb-2">
              ${t('charts.phishing.domains_found')}:
            </div>
            ${dataPoint.domains.join('<br/>')}
          </div>
        `;
      },
    },
    grid: { borderColor: '#ccc', row: { colors: ['transparent'] } },
    colors: ['#E53E3E'],
  };

  // Determinar ipInfoList según servicio:
  let ipInfoList: IPInfo[] = [];
  if (selectedService === 'all') {
    for (const svc of services) {
      const detail = serviceDetailsMap[svc.id];
      if (detail?.ip_info) {
        ipInfoList = ipInfoList.concat(detail.ip_info);
      }
    }
  } else {
    const foundService = services.find(
      s => s.domain.trim().toLowerCase() === selectedService.trim().toLowerCase()
    );
    if (foundService) {
      const detail = serviceDetailsMap[foundService.id];
      if (detail?.ip_info) {
        ipInfoList = detail.ip_info;
      }
    }
  }

  // Si no es "all", filtramos para que solo se muestren los registros cuyo dominio
  // esté en la lista de fake domains (filteredDomains)
  if (selectedService !== 'all') {
    const foundDomainsSet = new Set(filteredDomains.map(fd => fd.domain.trim().toLowerCase()));
    ipInfoList = ipInfoList.filter(
      info => info.domain && foundDomainsSet.has(info.domain.trim().toLowerCase())
    );
  }




  // Condiciones de loading, error y sin datos
  if (loading) return <Loader />;
  if (error) return <div className="text-center text-red-500">{error}</div>;
  if (!defaultDomains.length) {
    return (
      <div className="flex flex-col items-center justify-center bg-gray-50 text-gray-800 border border-gray-200 rounded-lg p-6 text-center">
        <Search className="w-10 h-10 mb-2 text-gray-500" />
        <p className="text-lg font-medium">{t('spoofing.no_data_title')}</p>
        <p className="text-sm text-gray-700 mt-2">
          {t('spoofing.no_data_description')}
        </p>
      </div>
    );
  }  

  return (
    <div className="bg-white shadow-md p-6">
      <h2 className="text-3xl font-extrabold text-gray-800 text-center mb-6">
        {t('charts.phishing.title')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
        {/* Celda 1: Lista con paginación */}
        <div className="flex flex-col h-full">
          <div className="mb-4 flex justify-center">
            <select
              id="serviceSelector"
              value={selectedService}
              onChange={(e) => {
                setSelectedService(e.target.value);
                setPage(1);
              }}
              className="p-2 rounded bg-[#0f172a] text-white font-semibold"
            >
              <option value="all">{t('charts.phishing.all_domains')}</option>
              {services.map((service) => (
                <option key={service.id} value={service.domain}>
                  {service.domain}
                </option>
              ))}
            </select>
          </div>

          {filteredDomains.length > 0 ? (
            <>
              <div className="flex flex-col items-center mb-3">
                <AlertCircle className="h-12 w-12 text-red-500 mb-2" />
                <p className="text-gray-800 text-lg font-bold text-center">
                  {t('charts.phishing.found', {
                    count: totalAttempts,
                    domain:
                      selectedService === 'all'
                        ? t('charts.phishing.active_domains')
                        : selectedService,
                  })}
                </p>
              </div>

              <ul className="flex-grow overflow-auto space-y-2">
                {pagedDomains.map((item, index) => (
                  <li
                    key={index}
                    className="bg-[#0f172a] text-white p-3 rounded-lg shadow"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-bold">{item.domain}</span>
                        {selectedService === 'all' && (
                          <span className="text-sm text-gray-300 ml-2">
                            ({t('charts.phishing.domain')}: {item.serviceDomain})
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-300">
                        {new Date(item.runDate).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage <= 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                  >
                    {t('charts.phishing.prev_page') || 'Anterior'}
                  </button>
                  <span className="text-sm">
                    {t('charts.phishing.page_x_of_y', {
                      x: currentPage,
                      y: totalPages,
                    }) || `Página ${currentPage} de ${totalPages}`}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                  >
                    {t('charts.phishing.next_page') || 'Siguiente'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-grow">
              <ShieldCheck className="h-12 w-12 text-green-500 mb-2" />
              <p className="text-gray-800 text-lg font-bold text-center">
                {t('charts.phishing.none', {
                  domain:
                    selectedService === 'all'
                      ? t('charts.phishing.active_domains')
                      : selectedService,
                })}
              </p>
            </div>
          )}
        </div>

        {/* Celda 2: Gráfica */}
        <div className="flex flex-col h-full">
          <h3 className="text-lg font-bold text-gray-700 mb-2 text-center">
            {t('charts.phishing.chart_title')}
          </h3>
          <div className="flex-grow flex items-center justify-center">
            {apexData.length > 0 ? (
              <ReactApexChart
                options={chartOptions}
                series={[{ name: 'Suplantaciones', data: apexData }]}
                type="bar"
                height={300}
                width={800}
              />
            ) : (
              <p className="text-gray-500 text-center">
                {t('charts.phishing.no_data')}
              </p>
            )}
          </div>
        </div>

        {/* Celda 3: Mapa */}
        <div className="flex flex-col h-full">
          <h2 className="text-lg font-bold mb-2">
            {t('charts.phishing.map_title') || 'Intentos de suplantación por país'}
          </h2>
          <div className="flex-grow flex items-center justify-center">
            {ipInfoList.length > 0 ? (
              <div className="w-full h-full">
                <IPCountriesMap ipInfoList={ipInfoList} />
              </div>
            ) : (
              <div className="bg-gray-100 flex-grow flex flex-col justify-center rounded p-4 text-center">
                <p className="text-gray-600 mb-2">
                  {t('charts.phishing.no_locations_found') || 'No se han encontrado ubicaciones'}
                </p>
                <div style={{ width: '100%', height: '400px' }} className="border border-gray-300 mx-auto" />
              </div>
            )}
          </div>
        </div>

        {/* Celda 4: TopCountries */}
        <div className="flex flex-col h-full">
          <h3 className="text-lg font-bold text-gray-700 mb-2 text-center">
            {t('charts.phishing.top_countries')}
          </h3>
          <div className="flex-grow flex items-center justify-center">
            {ipInfoList.length > 0 ? (
              <TopCountries ipInfoList={ipInfoList} />
            ) : (
              <p className="text-gray-600 text-center">
                {t('charts.phishing.no_locations_found')}
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PhishingAttempts;
